import { integrationApiService } from '../../services/integrationApi';
import type { RebateDiscountSettings } from '../../services/integrationApi';

export interface RebateDiscountSyncNeeded {
  instant: boolean;
  bundle: boolean;
}

/** A missing key means on, so only an explicit false is off. */
export const rebateDiscountOn = (
  settings: RebateDiscountSettings | undefined,
  kind: keyof RebateDiscountSettings,
): boolean => settings?.[kind] !== false;

/**
 * Which discount syncs a save must run: the ones whose switch changed, and whose
 * admin flag is on (with the flag off the server is already cleaning up).
 */
export function rebateDiscountSyncsNeeded(
  prev: RebateDiscountSettings | undefined,
  next: RebateDiscountSettings | undefined,
  flags: { instant?: boolean; bundle?: boolean },
): RebateDiscountSyncNeeded {
  return {
    instant: flags.instant === true && rebateDiscountOn(prev, 'instant') !== rebateDiscountOn(next, 'instant'),
    bundle: flags.bundle === true && rebateDiscountOn(prev, 'bundle') !== rebateDiscountOn(next, 'bundle'),
  };
}

/**
 * After the settings PUT: run the Shopify discount syncs for switches that
 * changed so a switch turned off takes its discounts down now. Never throws.
 */
export async function runRebateDiscountSyncs(
  integrationId: string,
  prev: RebateDiscountSettings | undefined,
  next: RebateDiscountSettings | undefined,
  flags: { instant?: boolean; bundle?: boolean },
): Promise<RebateDiscountSyncNeeded> {
  const needed = rebateDiscountSyncsNeeded(prev, next, flags);
  await Promise.all([
    needed.instant ? integrationApiService.syncShopifyRebateDiscounts(integrationId).catch(() => null) : null,
    needed.bundle ? integrationApiService.syncShopifyBundleDiscounts(integrationId).catch(() => null) : null,
  ]);
  return needed;
}
